// Settings › Login screen: may devices save Portal login details? (2026-10-29)
//
// Off by default — devices are shared, and a saved login signs the next
// person in as the last. Reads the live value from GET /login/methods (the
// same public answer the login page reads) and writes PATCH
// /login/saved-logins (Admin floor). Company-wide: staging and production
// share it, and each picks a change up within 30 s.

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { rememberSavedLogins, savedLoginsFrom } from "../login/savedLogins";

const SavedLoginsSetting = () => {
    const { t } = useTranslation();
    // null until GET /login/methods answers — the toggle stays disabled.
    const [allowed, setAllowed] = useState<boolean | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/login/methods`, { method: "GET" });
                if (!res?.ok) return;
                const value = savedLoginsFrom(await res.json().catch(() => null));
                if (!cancelled) setAllowed(value);
            } catch { /* leave it disabled */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const save = async (next: boolean) => {
        const previous = allowed;
        setAllowed(next);
        setSaving(true);
        try {
            const res = await apiFetch(`/login/saved-logins`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ allowed: next }),
            });
            if (!res?.ok) {
                setAllowed(previous);
                toast.error(t("saved_logins_save_failed"), { toastId: "saved-logins" });
                return;
            }
            rememberSavedLogins(next);
            toast.success(t(next ? "saved_logins_now_on" : "saved_logins_now_off"), { toastId: "saved-logins" });
        } catch {
            setAllowed(previous);
            toast.error(t("saved_logins_save_failed"), { toastId: "saved-logins" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-settings-table">
            <div className="admin-settings-row">
                <div>
                    <strong>{t("saved_logins_toggle")}</strong>
                    <div className="admin-settings-section-hint" style={{ margin: "4px 0 0", fontSize: "0.8rem" }}>
                        {t("saved_logins_toggle_hint")}
                    </div>
                </div>
                <label className="admin-settings-toggle">
                    <input
                        type="checkbox"
                        aria-label={t("saved_logins_toggle")}
                        checked={allowed === true}
                        disabled={allowed === null || saving}
                        onChange={e => save(e.target.checked)}
                    />
                    <span className="admin-settings-slider" />
                </label>
            </div>
        </div>
    );
};

export default SavedLoginsSetting;
